import React, { useEffect, useRef } from 'react';
import { X, AlertTriangle, Info, CheckCircle, HelpCircle } from 'lucide-react';

export type PopupVariant = 'info' | 'danger' | 'warning' | 'success' | 'question';
export type PopupButtonTone = 'primary' | 'danger' | 'gold' | 'secondary';

export interface PopupButton {
  /** Text shown on the button */
  label: string;
  /** Value the popup promise resolves with when this button is clicked */
  value?: any;
  /** Visual style of the button (defaults to 'primary') */
  tone?: PopupButtonTone;
  /** Focus this button when the popup opens (Enter triggers it) */
  autoFocus?: boolean;
}

export interface ModalProps {
  open: boolean;
  title?: string;
  message?: React.ReactNode;
  variant?: PopupVariant;
  buttons: PopupButton[];
  /** When true (default) the backdrop, Escape key, and X button dismiss the popup */
  dismissable?: boolean;
  onButtonClick: (value: any) => void;
  onDismiss: () => void;
}

const VARIANT_CONFIG: Record<PopupVariant, { Icon: React.ComponentType<{ size?: number }>; iconWrap: string }> = {
  info: { Icon: Info, iconWrap: 'bg-blue-500/10 text-blue-500' },
  danger: { Icon: AlertTriangle, iconWrap: 'bg-brand-danger/10 text-brand-danger' },
  warning: { Icon: AlertTriangle, iconWrap: 'bg-amber-500/10 text-amber-500' },
  success: { Icon: CheckCircle, iconWrap: 'bg-green-500/10 text-green-600' },
  question: { Icon: HelpCircle, iconWrap: 'bg-brand-gold/15 text-brand-gold' },
};

const TONE_CLASSES: Record<PopupButtonTone, string> = {
  primary: 'bg-brand-dark text-white hover:bg-navy-700',
  danger: 'bg-brand-danger text-white hover:bg-opacity-90',
  gold: 'bg-brand-gold text-brand-dark hover:bg-gold-500',
  secondary: 'bg-subtle text-body border border-line hover:bg-line/60',
};

/**
 * Presentational, reusable popup dialog. Renders a backdrop + card with an
 * icon, title, message, and an arbitrary set of action buttons. It holds no
 * state of its own — drive it directly, or via the `usePopup()` hook which
 * wraps it in an imperative confirm()/alert()/open() API.
 */
const Modal: React.FC<ModalProps> = ({
  open,
  title,
  message,
  variant = 'question',
  buttons,
  dismissable = true,
  onButtonClick,
  onDismiss,
}) => {
  const focusRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onDismiss();
    };
    document.addEventListener('keydown', onKey);

    // Prevent the page behind the popup from scrolling while it is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => focusRef.current?.focus(), 0);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
    };
  }, [open, dismissable, onDismiss]);

  if (!open) return null;

  const { Icon, iconWrap } = VARIANT_CONFIG[variant];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-brand-dark/50 backdrop-blur-sm animate-fadeIn"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && dismissable) onDismiss();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
    >
      <div className="bg-card w-full max-w-md rounded-2xl shadow-card-lg overflow-hidden animate-scaleIn border border-line">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl shrink-0 ${iconWrap}`}>
              <Icon size={24} />
            </div>
            <div className="flex-1 pt-1">
              {title && <h4 className="font-bold text-lg text-heading">{title}</h4>}
              {message && <div className="text-sm text-muted mt-1 leading-relaxed">{message}</div>}
            </div>
            {dismissable && (
              <button
                onClick={onDismiss}
                className="text-faint hover:text-body transition-colors shrink-0"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4 bg-subtle border-t border-line flex flex-wrap justify-end gap-3">
          {buttons.map((btn, idx) => (
            <button
              key={idx}
              ref={btn.autoFocus ? focusRef : undefined}
              onClick={() => onButtonClick(btn.value)}
              className={`px-4 py-2.5 rounded-lg font-bold text-sm transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/60 ${
                TONE_CLASSES[btn.tone || 'primary']
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Modal;
